import { useCallback, useEffect, useMemo, useState } from "react";
import { getTireDetailByProductCodeApi } from "../api/carModelApi";
import { ITireItemDetail } from "../types";
import Notify from "../../../config/notify";

/**
 * Custom React hook to fetch and manage tire detail data based on a product code.
 * @param {string} item_cd - The product code used to fetch tire details.
 * @returns {object} - An object containing the following properties:
 *   - `tireDetail` {ITireItemDetail | undefined}: The fetched tire detail data.
 *   - `loading` {boolean}: Indicates whether the data is currently being fetched.
 *   - `error` {boolean}: Indicates whether an error occurred during the fetch.
 *   - `refetch` {Function}: A function to manually refetch the tire detail data.
 */
export default function useProductCode(item_cd: string) {
  const [tireDetail, setTireDetail] = useState<ITireItemDetail>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  /**
   * Fetches tire detail data using the provided product code.
   * Handles loading state, error state, and updates the tire detail data.
   */
  const getTireDetail = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const data = await getTireDetailByProductCodeApi({ item_cd });

      if (!data) {
        setTireDetail({} as ITireItemDetail);
        Notify.setNotification({
          show: true,
          title: "選択されたタイヤサイズがありません",
          actionsPosition: 'center'
        });
        return;
      }

      const tireItemDetail = data.list.mst_item_detail_tire[0];
      setTireDetail(tireItemDetail);
    } catch (err) {
      setError(true);
      setTireDetail({} as ITireItemDetail);
    } finally {
      setLoading(false);
    }
  }, [item_cd]);

  // Automatically fetch tire detail data when the hook is used or `item_cd` changes.
  useEffect(() => {
    if (item_cd) {
      getTireDetail();
    } else {
      setTireDetail({} as ITireItemDetail);
    }
  }, [item_cd, getTireDetail]);

  // Memoize the returned object to prevent unnecessary re-renders.
  return useMemo(
    () => ({
      tireDetail,
      loading,
      error,
      refetch: getTireDetail,
    }),
    [tireDetail, loading, error, getTireDetail],
  );
}
